import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { usPhoneNumberValidation } from 'validators';
import { TextField, USPhoneNumberInput, useStateInRef } from 'seagull';
import { validateContact } from '~/business-logic/validators';
import { useLocaleKeys } from '~/i18n';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import s from './TriageContactForm.module.scss';
import { PrimaryContactRowTestIds } from './TriageContactRow';
const FOCUS_ORDER = [
    'firstName',
    'lastName',
    'companyName',
    'email',
    'phone'
];
const TriageContactFormInner = (props, ref)=>{
    var _contact_companyName, _contact_firstName, _contact_lastName, _contact_email, _contact_phone;
    const { contact, onFieldChange, triggerValidation } = props;
    const getProps = useStateInRef(props);
    const { t: primaryContactFormTexts } = useLocaleKeys('agreementImport.triageModal.primaryContactForm');
    const phoneValidator = usPhoneNumberValidation();
    const { control, trigger, setFocus, getFieldState, formState } = useForm({
        mode: 'all',
        defaultValues: {
            companyName: (_contact_companyName = contact.companyName) !== null && _contact_companyName !== void 0 ? _contact_companyName : '',
            firstName: (_contact_firstName = contact.firstName) !== null && _contact_firstName !== void 0 ? _contact_firstName : '',
            lastName: (_contact_lastName = contact.lastName) !== null && _contact_lastName !== void 0 ? _contact_lastName : '',
            email: (_contact_email = contact.email) !== null && _contact_email !== void 0 ? _contact_email : '',
            phone: (_contact_phone = contact.phone) !== null && _contact_phone !== void 0 ? _contact_phone : ''
        }
    });
    const isValid = formState.isValid;
    useEffect(()=>{
        getProps().onValidityChange(isValid);
    }, [
        isValid
    ]);
    useImperativeHandle(ref, ()=>({
            focusFirstInvalidField: async ()=>{
                const valid = await trigger();
                if (valid) return true;
                const firstInvalid = FOCUS_ORDER.find((field)=>getFieldState(field).invalid);
                if (firstInvalid != null) {
                    setFocus(firstInvalid);
                }
                return false;
            }
        }), [
        trigger,
        setFocus,
        getFieldState
    ]);
    useEffect(()=>{
        if (triggerValidation) {
            trigger();
        }
    }, [
        triggerValidation,
        trigger
    ]);
    const emitChange = (field, rhfOnChange)=>(e)=>{
            rhfOnChange(e);
            onFieldChange(field, e.target.value);
        };
    const connectorOptions = {
        alwaysShowErrors: !!triggerValidation
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: s.form,
        "data-testid": "triage-contact-form",
        children: [
            /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "firstName",
                rules: {
                    validate: validateContact.firstName
                },
                render: (renderProps)=>{
                    const connected = connectControllerToTextField(renderProps, connectorOptions);
                    return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                        bi: {
                            name: 'first_name'
                        }
                    }, connected), {
                        label: primaryContactFormTexts.firstName(),
                        testId: PrimaryContactRowTestIds.FirstNameInput(),
                        onChange: emitChange('firstName', connected.onChange)
                    }));
                }
            }),
            /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "lastName",
                rules: {
                    validate: validateContact.lastName
                },
                render: (renderProps)=>{
                    const connected = connectControllerToTextField(renderProps, connectorOptions);
                    return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                        bi: {
                            name: 'last_name'
                        }
                    }, connected), {
                        label: primaryContactFormTexts.lastName(),
                        testId: PrimaryContactRowTestIds.LastNameInput(),
                        onChange: emitChange('lastName', connected.onChange)
                    }));
                }
            }),
            /*#__PURE__*/ _jsx("div", {
                className: s.fullWidth,
                children: /*#__PURE__*/ _jsx(Controller, {
                    control: control,
                    name: "companyName",
                    rules: {
                        validate: validateContact.companyName
                    },
                    render: (renderProps)=>{
                        const connected = connectControllerToTextField(renderProps, connectorOptions);
                        return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                            bi: {
                                name: 'company_name'
                            }
                        }, connected), {
                            label: primaryContactFormTexts.companyName(),
                            testId: PrimaryContactRowTestIds.CompanyNameInput(),
                            onChange: emitChange('companyName', connected.onChange)
                        }));
                    }
                })
            }),
            /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "email",
                rules: {
                    validate: validateContact.email
                },
                render: (renderProps)=>{
                    const connected = connectControllerToTextField(renderProps, connectorOptions);
                    return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                        bi: {
                            name: 'email'
                        }
                    }, connected), {
                        label: primaryContactFormTexts.email(),
                        testId: PrimaryContactRowTestIds.EmailInput(),
                        onChange: emitChange('email', connected.onChange)
                    }));
                }
            }),
            /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "phone",
                rules: phoneValidator,
                render: (renderProps)=>{
                    const connected = connectControllerToTextField(renderProps, connectorOptions);
                    return /*#__PURE__*/ _jsx(USPhoneNumberInput, _object_spread_props(_object_spread({
                        bi: {
                            name: 'phone'
                        }
                    }, connected), {
                        label: primaryContactFormTexts.phone(),
                        testId: PrimaryContactRowTestIds.PhoneInput(),
                        onChange: emitChange('phone', connected.onChange)
                    }));
                }
            })
        ]
    });
};
export const TriageContactForm = /*#__PURE__*/ forwardRef(TriageContactFormInner);
